import React, { useEffect, useState, useRef } from "react";
import { fetchGlobalSettings } from "../api/globalsetting";
import "../assets/css/pages/Home.css";
import "../assets/css/Responsive/Home_Responsive.css";
import Lenis from "@studio-freight/lenis";

/**
 * Smooth scroll using Lenis
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        if (document.documentElement.style.scrollBehavior !== "auto") {
            document.documentElement.style.scrollBehavior = "auto";
        }

            const lenis = new Lenis({
                duration: 1.2,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            smoothWheel: true,
            smoothTouch: false,
        });

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        window.__lenis = lenis;

        return () => {
            lenis.destroy();
            if (window.__lenis === lenis) {
                delete window.__lenis;
            }
        };
    }, []);
};

// Enhanced AOS (Animate On Scroll) hook with stagger support
const useAOS = (faq, loading) => {
    useEffect(() => {
        if (!faq || loading) return;

        let observer = null;
        const observedElements = [];

        const timer = setTimeout(() => {
            const observerOptions = {
                threshold: 0.15,
                rootMargin: "0px 0px -100px 0px",
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    requestAnimationFrame(() => {
                        if (entry.isIntersecting) {
                            const section = entry.target;

                            if (!section.classList.contains("aos-animate")) {
                                section.classList.add("aos-animate");
                            }

                            const staggerElements =
                                section.querySelectorAll("[data-stagger-index]");

                            staggerElements.forEach((el, idx) => {
                                setTimeout(() => {
                                    if (el && !el.classList.contains("stagger-animate")) {
                                        el.classList.add("stagger-animate");
                                    }
                                }, idx * 10);
                            });
                        }
                    });
                });
            }, observerOptions);

            const sectionsToObserve = document.querySelectorAll(".aos-fade-up");
            sectionsToObserve.forEach((element) => {
                if (!element.classList.contains("aos-animate")) {
                    const rect = element.getBoundingClientRect();
                    const isInView =
                        rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

                    if (isInView) {
                        element.classList.add("aos-animate");
                        const staggerElements =
                            element.querySelectorAll("[data-stagger-index]");

                        staggerElements.forEach((el, idx) => {
                            setTimeout(() => {
                                if (el && !el.classList.contains("stagger-animate")) {
                                    el.classList.add("stagger-animate");
                                }
                            }, idx * 10);
                        });
                    } else {
                        observer.observe(element);
                        observedElements.push(element);
                    }
                }
            });
        }, 100);

        return () => {
            clearTimeout(timer);
            if (observer) {
                observedElements.forEach((element) => {
                    if (element) observer.unobserve(element);
                });
            }
        };
    }, [faq, loading]);
};

export default function Faq() {
    const [faq, setFaq] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openIndex, setOpenIndex] = useState(0); // Track which FAQ is open (first one by default)
    const [closingIndex, setClosingIndex] = useState(null); // Track which FAQ is closing
    const contentRefs = useRef({}); // Store refs for each FAQ content

    // Init Lenis smooth scroll
    useLenis();

    // Set page title
    useEffect(() => {
        document.title = "FAQ | Vancouver Lookout";
    }, []);

    // Fetch FAQ data
    useEffect(() => {
        let isMounted = true;

        setLoading(true);
        fetchGlobalSettings()
            .then((res) => {
                if (!isMounted) return;
                setFaq(res.faq || null);
                setLoading(false);
            })
            .catch((err) => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    // Add AOS fade-up class to sections
    useEffect(() => {
        if (!faq || loading) return;

        const sectionEls = document.querySelectorAll("section");
        sectionEls.forEach((el) => {
            if (!el.classList.contains("aos-fade-up")) {
                el.classList.add("aos-fade-up");
            }
        });
    }, [faq, loading]);

    // Initialize AOS animations
    useAOS(faq, loading);

    // Ultra-smooth height calculation - ZERO JERK
    const setContentHeight = (index, isOpen) => {
        const contentElement = contentRefs.current[index];
        if (!contentElement) return;

        if (isOpen) {
            // STEP 1: Get current computed height (even if 0)
            const currentHeight = contentElement.offsetHeight;
            
            // STEP 2: Temporarily disable ALL transitions for measurement
            contentElement.style.transition = 'none';
            contentElement.style.visibility = 'visible';
            contentElement.style.opacity = '0'; // Keep invisible during measurement
            
            // STEP 3: Measure actual content height
            contentElement.style.height = 'auto';
            const targetHeight = contentElement.scrollHeight;
            
            // STEP 4: Instantly reset to current/starting height (prevents flash)
            contentElement.style.height = currentHeight + 'px';
            
            // STEP 5: Force reflow to apply the instant reset
            void contentElement.offsetHeight;
            
            // STEP 6: Re-enable transitions
            contentElement.style.transition = '';
            contentElement.style.opacity = ''; // Allow opacity to animate
            
            // STEP 7: Use RAF to ensure smooth start of animation
            requestAnimationFrame(() => {
                contentElement.style.height = targetHeight + 'px';
            });
        } else {
            // CLOSE: Simply animate to 0
            contentElement.style.height = '0px';
        }
    };

    // Handle FAQ item click - ultra-smooth, zero jerk
    const handleFaqClick = (index) => {
        if (openIndex === index) {
            // CLOSE: Clicking same item
            setClosingIndex(index);
            setContentHeight(index, false);
            
            setTimeout(() => {
                setOpenIndex(null);
                setClosingIndex(null);
            }, 470); // Slightly before animation ends
        } else {
            // OPEN NEW: Clicking different item
            
            // Close the currently open item if exists
            if (openIndex !== null) {
                setClosingIndex(openIndex);
                setContentHeight(openIndex, false);
            }
            
            // Open the new item
            setOpenIndex(index);
            
            // Wait for state update and CSS classes to apply
            // Using setTimeout with 0 ensures DOM is updated before animation
            setTimeout(() => {
                setContentHeight(index, true);
            }, 10); // Small delay for smooth class application
            
            // Clear closing state
            setTimeout(() => {
                setClosingIndex(null);
            }, 470);
        }
    };

    // Set initial height for first FAQ if it's open
    useEffect(() => {
        if (openIndex !== null && faq && contentRefs.current[openIndex]) {
            // Wait for DOM to be ready, then measure and set height
            const timer = setTimeout(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setContentHeight(openIndex, true);
                    });
                });
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [faq, openIndex]);

    // Always render page structure - show content immediately when available
    if (!faq) {
        return (
            <div className="page-error">
                {/* No FAQs available at the moment. */}
            </div>
        );
    }

    return (
        <section className="faq" data-stagger-index="0">
            <div className="canvas">
                <div className="faq__inner" data-stagger-index="1">
                    <div className="title-sec" data-stagger-index="2">
                        <div className="faq__eyebrow">{faq.eyebrow}</div>
                        <h1 className="faq__title">{faq.title}</h1>
                    </div>

                    <div className="faq__list" data-stagger-index="3">
                        {faq.items?.map((item, index) => {
                            const isOpen = openIndex === index;
                            const isClosing = closingIndex === index;
                            return (
                                <div 
                                    className="faq-row" 
                                    key={index} 
                                    data-stagger-index={index + 1}
                                    onClick={() => handleFaqClick(index)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleFaqClick(index);
                                        }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className={`faq-item ${index === 0 ? "faq-item--lead" : ""} ${isOpen ? "faq-item--open" : ""} ${isClosing ? "faq-item--closing" : ""}`}>
                                        <div className="faq-item_summary">
                                            {item.question}
                                        </div>

                                        <div 
                                            ref={(el) => (contentRefs.current[index] = el)}
                                            className={`faq-item_content ${isOpen ? "faq-item_content--open" : ""} ${isClosing ? "faq-item_content--closing" : ""}`}
                                        >
                                            {item.answer}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

